import React, {useMemo, useState} from "react";
import {makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip} from "@material-ui/core";
import clsx from "clsx";
import Tr from 'translations/Translations';
import PropTypes from "prop-types";
import getObjectValue from "libs/getObjectValue";
import {ContractorCompanySizeLabel} from "constants/ContractorCompanySize";
import WorkableIconEditButton from "components/button/WorkableIconEditButton";
import WorkableIconDeleteButton from "components/button/WorkableIconDeleteButton";
import Typography from "components/Typography";
import InviteContactor from "components/popup/inviteContactor/InviteContactor";
import Button from "components/Button";
import {useParams} from "react-router";
import ErrorIcon from "@material-ui/icons/Error";
import {connect} from "react-redux";

const useStyles = makeStyles(theme => ({
    table: {
        tableLayout: 'fixed',
        '& td, th': {
            overflow: 'hidden',
        }
    },
    cell: {
        padding: theme.spacing(1)
    },
    emailColumn: theme.components.table.emailColumn,
    commonColumn: theme.components.table.dateColumn,
    buttonColumn: theme.components.table.buttonColumn,
    addButton: {
        marginTop: theme.spacing(),
        marginBottom: theme.spacing(2),
    },
    vatinInline: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        minWidth: 0,
    },
    vatinTooltip: {
        marginRight: theme.spacing(0.5),
        display: 'inline-flex',
        flexShrink: 0,
        verticalAlign: 'middle',
    },
    vatinText: {
        minWidth: 0,
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
    }
}));

function ConsorciumTable(
    {
        id,
        formikProps,
        contractor,
        companySize,
        disabled,
        commonDataErrors,
        offerPositionFormId,
        positionFormId
    }) {

    const classes = useStyles();
    const params = useParams();
    const [invitePopup, setInvitePopup] = useState(null);

    const getInvalidVatinIndexes = (errorsObj) => {
        if (!errorsObj || typeof errorsObj !== 'object') return [];

        return Object.entries(errorsObj)
            .filter(([, value]) => value === 'vatinwrongvalue')
            .map(([key]) => {
                const match = key.match(/consorciants\[(\d+)\]\.vatin(\.|$)/);
                return match ? Number(match[1]) : null;
            })
            .filter((idx) => Number.isInteger(idx));
    };
    const invalidVatinIndexes = useMemo(() => getInvalidVatinIndexes(commonDataErrors), [commonDataErrors]);

    const handleInvite = (formikProps, data) => {
        if (!data.identity) {
            let identity = -1;
            if (Array.isArray(formikProps.values.consorciants) && formikProps.values.consorciants.length > 0) {
                const lastId = formikProps.values.consorciants[formikProps.values.consorciants.length - 1].identity;
                if (lastId < 0) {
                    identity = lastId - 1;
                }
            }

            formikProps.setFieldValue("consorciants", [...(formikProps.values.consorciants || []), {
                ...data,
                identity: identity,
                supplierId: 0,
                positionId: null,
                status: "NEW"
            }])
        } else
            formikProps.setFieldValue("consorciants", formikProps.values.consorciants.map(x => data.identity === x.identity ? {...x, ...data} : x))
        setInvitePopup(null);
    }


    const removeContractor = (c) => {
        formikProps.setFieldValue(id, formikProps.values[id].filter(x => x.identity !== c.identity))
    }

    const error = getObjectValue(id, formikProps.errors, null);

    return (<>

            <TableContainer>
                <Table className={clsx(classes.table)}>
                    <TableHead>
                        <TableRow>
                            <TableCell className={clsx(classes.cell)}>{Tr("consorciant_name")}</TableCell>
                            <TableCell className={clsx(classes.cell, classes.emailColumn)}>{Tr("address")}</TableCell>
                            <TableCell className={clsx(classes.cell, classes.commonColumn)}>{Tr("nip_pesel")}</TableCell>
                            <TableCell className={clsx(classes.cell, classes.commonColumn)}>{Tr("regon")}</TableCell>
                            <TableCell className={clsx(classes.cell, classes.commonColumn)}>{Tr("contractor_company_size")}</TableCell>
                            <TableCell className={clsx(classes.cell, classes.emailColumn)}>{Tr("email")}</TableCell>
                            {!disabled && <TableCell className={clsx(classes.cell, classes.buttonColumn)}></TableCell>}
                            {!disabled && <TableCell className={clsx(classes.cell, classes.buttonColumn)}></TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell className={classes.cell}>{contractor.name}</TableCell>
                            <TableCell className={classes.cell}>
                                {contractor.address}<br/>
                                {contractor.postCode} {contractor.city}
                            </TableCell>
                            <TableCell className={classes.cell}>
                                {contractor.vatin?.prefix} {contractor.vatin?.number}
                            </TableCell>
                            <TableCell className={classes.cell}>
                                {contractor.regon}
                            </TableCell>
                            <TableCell className={classes.cell}>
                                {!!companySize && ContractorCompanySizeLabel(companySize)}
                            </TableCell>
                            <TableCell className={classes.cell}>
                                {contractor.email}
                            </TableCell>
                            {!disabled && <TableCell className={classes.buttonColumn}>
                                <WorkableIconEditButton disabled/>
                            </TableCell>}
                            {!disabled && <TableCell className={classes.buttonColumn}>
                                <WorkableIconDeleteButton disabled/>
                            </TableCell>}
                        </TableRow>
                        {!!formikProps.values[id] && formikProps.values[id].map((c, index) => (<TableRow key={c.identity}>
                            <TableCell className={classes.cell}>{c.name}</TableCell>
                            <TableCell className={classes.cell}>
                                {c.address}<br/>
                                {c.postCode} {c.city}
                            </TableCell>
                            <TableCell className={classes.cell}>
                                <span className={classes.vatinInline}>
                                    {invalidVatinIndexes.includes(index) && positionFormId === offerPositionFormId && (
                                        <Tooltip title={Tr('vatinwrongvalue')} className={classes.vatinTooltip}>
                                            <ErrorIcon color='error' fontSize='small' />
                                        </Tooltip>
                                    )}
                                    <span className={classes.vatinText}>
                                        {c.vatin?.prefix} {c.vatin?.number}
                                    </span>
                                </span>
                            </TableCell>
                            <TableCell className={classes.cell}>
                                {c.regon}
                            </TableCell>
                            <TableCell className={classes.cell}>
                                {!!c.companySize && ContractorCompanySizeLabel(c.companySize)}
                            </TableCell>
                            <TableCell className={classes.cell}>
                                {c.email}
                            </TableCell>
                            {!disabled && <TableCell className={classes.buttonColumn}>
                                <WorkableIconEditButton onClick={() => setInvitePopup(c)}/>
                            </TableCell>}
                            {!disabled && <TableCell className={classes.buttonColumn}>
                                <WorkableIconDeleteButton onClick={() => removeContractor(c)}/>
                            </TableCell>}
                        </TableRow>))
                        }
                    </TableBody>
                </Table>
                {!!error && <Typography className={'MuiFormHelperText-root Mui-error'} color='error'>{error}</Typography>}
            </TableContainer>
            {!disabled && <Button className={classes.addButton}  onClick={() => setInvitePopup({})}>{Tr('add_consorciant')}</Button>}
            {!!invitePopup && <InviteContactor
                demandId={params.id}
                open={true}
                onClose={() => setInvitePopup(null)}
                handleInvite={(data) => handleInvite(formikProps, data)}
                defaultValues={invitePopup}
                checkForExistingVatins={false}
                collectCity
                collectAddress
                collectRegon
                collectCompanySize
                collectPostCode
                collectIdentity
                contractorList={{
                    items: formikProps.values.consorciants ? formikProps.values.consorciants.map(x => ({
                        id: x.identity,
                        contractor: {
                            ...x
                        }
                    })) : []
                }}
                submitButtonLabel={!invitePopup.identity ? 'add' : 'zapisz'}
                title={Tr('consorcium')}
            />}
        </>
    )
}

ConsorciumTable.propTypes = {
    id: PropTypes.string.isRequired,
    formikProps: PropTypes.object.isRequired,
    contractor: PropTypes.object.isRequired,
    disabled: PropTypes.bool.isRequired
}


export default connect((state) => ({
    commonDataErrors: state.resources.commonDataErrors,
    offerPositionFormId: state.resources.offerPositionFormId
}), {})(ConsorciumTable);

// memo(ConsorciumTable, (prevProps, nextProps) => {
//     if (prevProps.formikProps.values !== nextProps.formikProps.values) return false;
//     if (prevProps.formikProps.errors !== nextProps.formikProps.errors) return false;
//     return true;
// })